import React, { useState } from 'react';
import { Header } from './components/Header';
import { IntroScreen } from './components/IntroScreen';
import { TopicScreen } from './components/TopicScreen';
import { useSwipeNavigation } from './hooks/useSwipeNavigation';
import { AlertCircleIcon, TrendingUpIcon, AwardIcon, UsersIcon } from 'lucide-react';
const TOPIC_DATA = [{
  heroImage: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&q=80',
  heroAlt: 'Frustrated clinician reviewing patient charts',
  quiz: {
    question: 'How many distinct mechanisms of action are available across standard anti-VEGF treatments?',
    options: [{
      text: '1–2',
      isCorrect: true,
      feedback: 'Most current options target VEGF-A only.'
    }, {
      text: '3–4',
      isCorrect: false,
      feedback: 'Actually, most standard treatments share the same VEGF-A mechanism.'
    }, {
      text: '5+',
      isCorrect: false,
      feedback: 'The mechanistic diversity is more limited than this suggests.'
    }]
  },
  poll: {
    percentage: 67
  },
  evidence: {
    content: "In current retinal practice, clinicians face narrow mechanistic options. When one anti-VEGF treatment plateaus, switching offers limited incremental benefit — the underlying mechanism is identical. The challenge isn't a lack of products — it's a lack of mechanistic diversity."
  },
  deepDive: {
    title: 'Why Same Mechanism = Same Limits',
    subtitle: 'Anti-VEGF mechanism comparison',
    icon: <AlertCircleIcon className="w-6 h-6 text-roche-blue" data-id="element-0" />
  },
  cta: {
    text: 'Discover the Difference'
  }
}, {
  heroImage: 'https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?w=800&q=80',
  heroAlt: 'Confident ophthalmologist in modern clinic with patient',
  quiz: {
    question: 'What makes Vabysmo structurally unique?',
    options: [{
      text: 'Higher concentration',
      isCorrect: false,
      feedback: "Concentration isn't the key differentiator here."
    }, {
      text: 'Bispecific antibody',
      isCorrect: true,
      feedback: 'Vabysmo targets both VEGF-A and Ang-2 simultaneously.'
    }, {
      text: 'Extended half-life',
      isCorrect: false,
      feedback: 'While important, the bispecific mechanism is the fundamental innovation.'
    }]
  },
  poll: {
    percentage: 54
  },
  evidence: {
    content: 'Vabysmo (faricimab) is the first and only bispecific antibody for retinal conditions. It simultaneously inhibits two distinct pathways: VEGF-A and Ang-2 — addressing both vascular leakage and vessel instability.'
  },
  deepDive: {
    title: 'How Bispecific Targeting Works',
    subtitle: 'VEGF-A + Ang-2 dual mechanism',
    icon: <TrendingUpIcon className="w-6 h-6 text-roche-blue" data-id="element-1" />
  },
  cta: {
    text: 'See the Clinical Evidence'
  }
}, {
  heroImage: 'https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=800&q=80',
  heroAlt: 'Doctor showing positive results to relieved patient',
  quiz: {
    question: 'How frequently could some Vabysmo patients extend treatment intervals to?',
    options: [{
      text: 'Every 4 weeks',
      isCorrect: false,
      feedback: 'Vabysmo enables longer intervals than this.'
    }, {
      text: 'Every 8 weeks',
      isCorrect: false,
      feedback: 'Some patients can extend even further.'
    }, {
      text: 'Up to every 16 weeks',
      isCorrect: true,
      feedback: 'Up to 16 weeks — significantly reducing treatment burden.'
    }]
  },
  poll: {
    percentage: 38
  },
  evidence: {
    content: 'In TENAYA/LUCERNE trials, Vabysmo demonstrated non-inferior visual acuity gains with potential for extended treatment intervals of up to 16 weeks. Fewer injections, reduced burden, improved quality of life.'
  },
  deepDive: {
    title: 'TENAYA/LUCERNE Trial Results',
    subtitle: 'Efficacy and dosing data',
    icon: <AwardIcon className="w-6 h-6 text-roche-blue" data-id="element-2" />
  },
  cta: {
    text: 'See How Peers Are Responding'
  }
}, {
  heroImage: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=800&q=80',
  heroAlt: 'Group of clinicians in collaborative discussion',
  quiz: {
    question: 'What is the primary benefit clinicians report from a bispecific approach?',
    options: [{
      text: 'Lower cost',
      isCorrect: false,
      feedback: "Cost isn't the primary clinical benefit reported."
    }, {
      text: 'Mechanistic diversity',
      isCorrect: true,
      feedback: 'Clinicians value having a fundamentally different treatment option.'
    }, {
      text: 'Faster onset',
      isCorrect: false,
      feedback: 'The key benefit is the unique dual-pathway mechanism.'
    }]
  },
  poll: {
    percentage: 72
  },
  evidence: {
    content: "Clinicians increasingly recognise that mechanistic diversity matters. Vabysmo offers a fundamentally new approach that doesn't compete on the same axis as existing treatments."
  },
  deepDive: {
    title: 'Vabysmo in Your Practice',
    subtitle: 'Practical integration considerations',
    icon: <UsersIcon className="w-6 h-6 text-roche-blue" data-id="element-3" />
  },
  cta: {
    text: 'Take the Next Step — Book a Consultation'
  }
}];
export function App() {
  const [showIntro, setShowIntro] = useState(true);
  const {
    currentTopic,
    goToTopic,
    containerRef
  } = useSwipeNavigation({
    totalTopics: TOPIC_DATA.length
  });
  const handleBeginJourney = () => {
    setShowIntro(false);
  };
  const handleTopicCTA = () => {
    if (currentTopic < TOPIC_DATA.length - 1) {
      goToTopic(currentTopic + 1);
    }
  };
  if (showIntro) {
    return <div className="w-full max-w-mobile mx-auto bg-white" data-id="element-4">
        <IntroScreen onBegin={handleBeginJourney} data-id="element-5" />
      </div>;
  }
  return <div className="w-full max-w-mobile mx-auto bg-white relative" data-id="element-6">
      <Header currentTopic={currentTopic} totalTopics={TOPIC_DATA.length} data-id="element-7" />

      {/* Topic container with horizontal scroll */}
      <div ref={containerRef} className="flex overflow-x-auto snap-container scrollbar-hide pt-32" style={{
      scrollbarWidth: 'none',
      msOverflowStyle: 'none'
    }} data-id="element-8">
        {TOPIC_DATA.map((topic, index) => <div key={index} className="w-full flex-shrink-0 snap-item" style={{
        width: '100%'
      }} data-id="element-9">
            <TopicScreen data={{
          ...topic,
          cta: {
            ...topic.cta,
            onClick: handleTopicCTA
          }
        }} data-id="element-10" />
          </div>)}
      </div>

      {/* Hide scrollbar */}
      <style data-id="element-11">{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>;
}